import type {
    CreateSearchArgs,
    CancelSearchArgs,
    ISearchModule,
    RequestParams,
    UpdateSearchArgs,
    DataSourceSubscription,
} from '@splunk/dashboard-types';
import { DS_STATUS } from '@splunk/datasource-utils';
import type { ConstructableSearchModuleArgs } from './types';

const defaultInitialRequestParams: RequestParams = { offset: 0, count: 1000 };

export class SearchModule implements ISearchModule<DataSourceSubscription> {
    private readonly registry: ConstructableSearchModuleArgs['registry'];

    constructor({ registry }: ConstructableSearchModuleArgs) {
        this.registry = registry;
    }

    /**
     * Cancels the subscription to a search. Will tear down search if there are no remaining subscribers
     * @param {string} param0.dataSourceId An identifier for the datasource
     * @param {string} param0.consumerId An identifier for the consumer + binding
     */
    // eslint-disable-next-line class-methods-use-this
    cancel({ subscription }: CancelSearchArgs) {
        // Will clean up both viz/input and chain search subscriptions automatically
        // Will tear down controller if no subscriptions remain
        subscription.cancel();
    }

    // Operations within this method may throw errors originating from datasource controllers,
    // these are intentionally not caught here as they would just be re-thrown to the caller
    async create({
        consumerId,
        dataSourceId,
        initialRequestParams = defaultInitialRequestParams,
        onData,
        onError,
    }: CreateSearchArgs) {
        const controller = this.registry.allocate({ dataSourceId });
        const subscription = await controller.subscribe({
            consumerId,
            initialRequestParams,
        });
        subscription.subscribeToData({ onData, onError });
        const statusSubscription = controller
            .getStatusObservable()
            .subscribe(({ error, meta }) => {
                if (error) {
                    onError(error);
                } else {
                    onData({ meta });
                }
            });

        const { updateRequestParams } = subscription;
        //
        const {
            refresh,
            control,
            // included for backwards compatibility
            getPauseStatus, // NOSONAR
            supportsControlActions,
        } = controller;

        const cancelSubscription = () => {
            statusSubscription.unsubscribe();
            subscription.cancel();
        };

        return {
            // requires .bind to ensure `this` correctly refers to the instantiated class
            updateRequestParams: updateRequestParams.bind(subscription),
            cancel: cancelSubscription,
            refresh: refresh.bind(controller),
            getInitialCallbacks: () => ({ onData, onError }),
            getLastState: () => subscription.getLastState(),
            control: control.bind(controller),
            getPauseStatus: getPauseStatus.bind(controller),
            supportsControlActions: supportsControlActions.bind(controller),
        };
    }

    async update({
        consumerId,
        dataSourceId,
        subscription,
        initialRequestParams = defaultInitialRequestParams,
    }: UpdateSearchArgs<DataSourceSubscription>) {
        const { onData, onError } = subscription.getInitialCallbacks();

        const lastSubscriptionState = subscription.getLastState();
        onData({
            ...lastSubscriptionState,
            meta: { ...lastSubscriptionState.meta, status: DS_STATUS.QUEUED },
        });
        this.cancel({ subscription });

        return this.create({
            consumerId,
            dataSourceId,
            initialRequestParams,
            onData,
            onError,
        });
    }
}
